/**
 * WebSocket Event Bridge Hook
 * Bridges custom DOM events (ws-send, ws-message) to the BrowserClient
 * This allows components like groups-panel to use custom events while maintaining the underlying WebSocket connection
 */

import { useEffect, useRef } from 'react';
import type { BrowserClient } from './client';

/**
 * Sets up event bridge between custom DOM events and WebSocket client
 * Allows:
 * - Sending: window.dispatchEvent(new CustomEvent('ws-send', { detail: message }))
 * - Receiving: window.addEventListener('ws-message', handler)
 *
 * Accepts the client directly to avoid creating a second BrowserClient instance
 * (which would double WS connections and cause duplicate re-renders on WS events).
 */
export function useWSEventBridge(client: BrowserClient | null): void {
  const isSetupRef = useRef(false);

  useEffect(() => {
    if (!client || isSetupRef.current) {
      return;
    }

    isSetupRef.current = true;

    // Listen for custom 'ws-send' events and forward to WebSocket
    const handleWSSend = (event: Event) => {
      if (event instanceof CustomEvent) {
        const message = event.detail;
        if (message && message.type) {
          // Send through the WebSocket client based on message type
          switch (message.type) {
            case 'get_user_groups':
              client.getUserGroups(message.adjective, message.noun);
              break;
            case 'list_groups':
              client.listGroups(message.groupType);
              break;
            case 'enter_group_sub_accord':
              client.enterGroupSubAccord(message.groupId, message.subAccordType);
              break;
            case 'get_my_group_role':
              client.getMyGroupRole(message.groupId);
              break;
            // Add more cases as needed
            default:
              console.warn('[WSEventBridge] Unknown message type:', message.type);
          }
        }
      }
    };

    // Listen for WebSocket messages and dispatch custom 'ws-message' events
    const handleUserGroups = (groups: any) => {
      window.dispatchEvent(
        new CustomEvent('ws-message', { detail: { type: 'user_groups', groups } })
      );
    };

    const handleGroupsList = (groups: any) => {
      window.dispatchEvent(
        new CustomEvent('ws-message', { detail: { type: 'groups_list', groups } })
      );
    };

    const handleEnteredGroupSubAccord = (subAccordId: string) => {
      window.dispatchEvent(
        new CustomEvent('ws-message', { detail: { type: 'entered_group_sub_accord', subAccordId } })
      );
    };

    const handleMyGroupRole = (groupId: string, role: string) => {
      window.dispatchEvent(
        new CustomEvent('ws-message', { detail: { type: 'my_group_role', groupId, role } })
      );
    };

    const handleError = (message: string) => {
      window.dispatchEvent(new CustomEvent('ws-message', { detail: { type: 'error', message } }));
    };

    // Add listeners
    window.addEventListener('ws-send', handleWSSend);
    client.on('user_groups', handleUserGroups);
    client.on('groups_list', handleGroupsList);
    client.on('entered_group_sub_accord', handleEnteredGroupSubAccord);
    client.on('my_group_role', handleMyGroupRole);
    client.on('error', handleError);

    // Cleanup
    return () => {
      window.removeEventListener('ws-send', handleWSSend);
      client.off('user_groups', handleUserGroups);
      client.off('groups_list', handleGroupsList);
      client.off('entered_group_sub_accord', handleEnteredGroupSubAccord);
      client.off('my_group_role', handleMyGroupRole);
      client.off('error', handleError);
    };
  }, []);
}
